// SHA: eligibility, the SHA visit and settings (docs/plans/phase1-sha.md §8.3).
import { queryOptions } from '@tanstack/react-query';
import {
  facilitySearchResponseSchema,
  practitionerSearchResponseSchema,
  shaSetupSchema,
  userSummarySchema,
  pageSchema,
  type FacilitySearchRequest,
  type PractitionerSearchRequest,
  eligibilityCheckListSchema,
  eligibilityCheckViewSchema,
  shaConnectionSchema,
  shaSchemeListSchema,
  shaSettingsSchema,
  visitShaSchema,
  type EligibilityCheckRequest,
  type ManualEligibilityRequest,
  type UpdateShaSettingsRequest,
} from '@wodi/shared';

import { api } from './client.ts';

export const shaKeys = {
  connection: ['sha', 'connection'] as const,
  schemes: ['sha', 'schemes'] as const,
  checks: (patientId: string) => ['patients', patientId, 'eligibility-checks'] as const,
  visit: (visitId: string) => ['visits', visitId, 'sha'] as const,
  settings: ['sha', 'settings'] as const,
  setup: ['sha', 'setup'] as const,
  staff: ['sha', 'staff'] as const,
};

export const shaConnectionQuery = queryOptions({
  queryKey: shaKeys.connection,
  queryFn: () => api('GET', '/sha/connection', { schema: shaConnectionSchema }),
  staleTime: 300_000,
});

export const shaSchemesQuery = queryOptions({
  queryKey: shaKeys.schemes,
  queryFn: () => api('GET', '/sha/schemes', { schema: shaSchemeListSchema }),
});

export const eligibilityChecksQuery = (patientId: string) =>
  queryOptions({
    queryKey: shaKeys.checks(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/eligibility-checks`, {
        schema: eligibilityCheckListSchema,
      }),
  });

export const visitShaQuery = (visitId: string) =>
  queryOptions({
    queryKey: shaKeys.visit(visitId),
    queryFn: () => api('GET', `/visits/${visitId}/sha`, { schema: visitShaSchema }),
  });

export function checkEligibility(patientId: string, body: EligibilityCheckRequest) {
  return api('POST', `/patients/${patientId}/eligibility-checks`, {
    body,
    schema: eligibilityCheckViewSchema,
  });
}

export function recordManualEligibility(patientId: string, body: ManualEligibilityRequest) {
  return api('POST', `/patients/${patientId}/eligibility-checks/manual`, {
    body,
    schema: eligibilityCheckViewSchema,
  });
}

export function startShaVisit(visitId: string) {
  return api('POST', `/visits/${visitId}/sha/start`, { schema: visitShaSchema });
}

export const shaSettingsQuery = queryOptions({
  queryKey: shaKeys.settings,
  queryFn: () => api('GET', '/admin/sha-settings', { schema: shaSettingsSchema }),
});

export function updateShaSettings(body: UpdateShaSettingsRequest) {
  return api('PUT', '/admin/sha-settings', { body, schema: shaSettingsSchema });
}

export const shaSetupQuery = queryOptions({
  queryKey: shaKeys.setup,
  queryFn: () => api('GET', '/sha/setup', { schema: shaSetupSchema }),
});

/** Staff who can be linked to the Health Worker Registry (the first 100 users). */
export const staffQuery = queryOptions({
  queryKey: shaKeys.staff,
  queryFn: () => api('GET', '/users?limit=100', { schema: pageSchema(userSummarySchema) }),
});

export function searchFacilityRegistry(body: FacilitySearchRequest) {
  return api('POST', '/registries/facilities/search', {
    body,
    schema: facilitySearchResponseSchema,
  });
}

export function searchPractitionerRegistry(body: PractitionerSearchRequest) {
  return api('POST', '/registries/practitioners/search', {
    body,
    schema: practitionerSearchResponseSchema,
  });
}

export function linkFacilityToRegistry(facilityId: string, registryId: string) {
  return api('PUT', `/admin/facilities/${facilityId}/registry-link`, {
    body: { registryId },
    schema: shaSetupSchema,
  });
}

export function refreshFacilityLink(facilityId: string) {
  return api('POST', `/admin/facilities/${facilityId}/registry-link/refresh`, {
    schema: shaSetupSchema,
  });
}

export function activatePreauthList(listId: string) {
  return api('POST', `/sha/preauth-lists/${listId}/activate`, { schema: shaSetupSchema });
}

export function linkPractitionerToRegistry(userId: string, registryId: string) {
  return api('PUT', `/admin/practitioners/${userId}/registry-link`, {
    body: { registryId },
    schema: shaSetupSchema,
  });
}
